import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, isElevated } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Plus, Search, Filter, Trash2, RotateCcw, AlertTriangle, Car as CarIcon,
  ChevronUp, ChevronDown, ChevronsUpDown, Globe,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";
import StickyHScroll from "@/components/StickyHScroll";
import CopertinaThumb, { copertinaId } from "@/components/CopertinaThumb";
import MostraFotoToggle, { useMostraFoto } from "@/components/MostraFotoToggle";
import { rigaCliccabile, RIGA_CLS } from "@/lib/rigaCliccabile";
import { Cella, fmtData, useLarghezzeAuto, useUltimaRichiesta, attrRicerca } from "@/lib/listaColonne";
import DisponibilitaBadge from "@/components/DisponibilitaBadge";
import VehicleStatusBadge from "@/components/VehicleStatusBadge";
import DispFisicaBadge from "@/components/DisponibilitaBadge";
import TipoCaricoBadge from "@/components/TipoCaricoBadge";
import VehicleColumnsButton from "@/components/VehicleColumnsButton";
import { formatCurrencyIT } from "@/components/CurrencyInput";
import {
  VEHICLE_STATUS_LABELS, VEHICLE_STATO_LABELS, VEHICLE_CARBURANTE_LABELS,
  VEHICLE_CAMBIO_LABELS, VEHICLE_TRAZIONE_LABELS, VEHICLE_TIPO_COLORE_LABELS,
  VEHICLE_DEPOSITO_LABELS, VEHICLE_TIPO_CARICO_LABELS,
} from "@/lib/labels";

const PAGE_SIZES = [25, 50, 100, 200];

const fmtEur = (v) =>
  v == null || v === ""
    ? "—"
    : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
const fmtKm = (v) =>
  v == null || v === "" ? "—" : new Intl.NumberFormat("it-IT").format(v) + " km";
const yearFromDate = (d) => (d ? String(d).slice(0, 4) : "—");
const fmtDate = fmtData;
// Sprint DASHBOARD-FIX-2 — colonne di servizio in carattere piccolo (misura di «Data contratto» in Pratiche)
const SERVIZIO = new Set(["codice", "data_carico", "prima_immatricolazione", "scadenza_tassa_proprieta",
  "prossima_revisione_data", "prossimo_tagliando", "garanzia_casa_madre_scadenza", "company", "site", "deposito"]);
const truncate = (s, n) => (!s ? "—" : s.length > n ? s.slice(0, n) + "…" : s);

// ----- Column registry (Sprint 2B.1 + 2B.1.1 + 4Z.2) -----
// sortField: backend mongo path; null/undefined = not sortable
// Sprint 4Z.2 E1: TipoCaricoBadge come PRIMA colonna (alwaysShow, fissa).
// Sprint 4Z.2 B4+B5+E2: 2 colonne SEPARATE Disp. e Stato (no badge combinato).
const COLUMN_DEFS = [
  { key: "tipo_carico_badge", label: "T.C.", sortField: "tipo_carico", alwaysShow: true,
    render: (v) => <TipoCaricoBadge value={v.tipo_carico} size="sm" /> },
  { key: "disp_fisica", label: "Disp.", sortField: "disponibilita_fisica",
    render: (v) => <DispFisicaBadge status={v.disponibilita_fisica} size="sm" /> },
  { key: "stato_commerciale", label: "Stato", sortField: "stato_commerciale",
    render: (v) => {
      const sc = v.stato_commerciale_corrente;
      // Sprint 4Z.2.1: nessuna pratica attiva → badge "Vendibile" (emerald light)
      if (!sc || sc === "draft") {
        return (
          <span className="inline-flex items-center font-medium rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 px-2 py-0.5 text-[11px]"
                data-testid="stato-pill-vendibile">
            Vendibile
          </span>
        );
      }
      return <VehicleStatusBadge stato_commerciale={sc} size="sm" />;
    } },
  { key: "codice", label: "Codice", sortField: "codice",
    render: (v) => <span className="font-mono text-blue-700">{v.codice}</span> },
  { key: "targa", label: "Targa", sortField: "targa",
    render: (v) => v.targa || "—" },
  { key: "marca", label: "Marca", sortField: "marca",
    render: (v) => <span className="font-medium">{v.marca}</span> },
  { key: "modello", label: "Modello", sortField: "modello",
    render: (v) => v.modello },
  { key: "anno", label: "Anno", sortField: "prima_immatricolazione",
    render: (v) => yearFromDate(v.prima_immatricolazione) },
  { key: "km", label: "Km", sortField: "km",
    render: (v) => fmtKm(v.km) },
  { key: "carburante", label: "Carburante", sortField: "carburante",
    render: (v) => VEHICLE_CARBURANTE_LABELS[v.carburante] || "—" },
  { key: "cambio", label: "Cambio", sortField: "cambio",
    render: (v) => VEHICLE_CAMBIO_LABELS[v.cambio] || "—" },
  { key: "colore", label: "Colore", sortField: "colore",
    render: (v) => v.colore || "—" },
  { key: "kw_cv", label: "KW / CV", sortField: "kw",
    render: (v) => (v.kw ? `${v.kw} / ${v.cv ?? "—"}` : "—") },
  { key: "prezzo_web", label: "Prezzo Web", sortField: "importi.prezzo_web",
    render: (v) => {
      const recovery = v.importi?.recupero_su_promo || 0;
      return (
        <div className="flex items-center gap-1">
          {fmtEur(v.importi?.prezzo_web)}
          {recovery > 0 && (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title="Sotto soglia margine" />
          )}
        </div>
      );
    } },
  { key: "site", label: "Sede", sortField: "sede_nome",
    render: (v, ctx) => ctx.allSites.find((s) => s.id === v.site_id)?.nome || "—" },
  { key: "company", label: "Azienda", sortField: "azienda_nome",
    render: (v, ctx) => {
      const c = ctx.companies.find((c) => c.id === v.company_id);
      return c?.nome_breve || c?.name || "—";
    } },
  { key: "giorni_stock", label: "Giorni giacenza", sortField: "giorni_giacenza",
    render: (v) => v.giorni_giacenza ?? v.giorni_stock ?? "—" },
  { key: "data_carico", label: "Data carico", sortField: "data_carico",
    render: (v) => fmtDate(v.data_carico) },

  // ---- hidden by default — Caratteristiche ----
  { key: "telaio", label: "Telaio", sortField: "telaio", defaultHidden: true,
    render: (v) => <span className="font-mono text-xs">{v.telaio || "—"}</span> },
  { key: "versione", label: "Versione", sortField: "versione", defaultHidden: true,
    render: (v) => v.versione || "—" },
  { key: "allestimento", label: "Allestimento", sortField: "allestimento", defaultHidden: true,
    render: (v) => v.allestimento || "—" },
  { key: "cilindrata", label: "Cilindrata", sortField: "cilindrata", defaultHidden: true,
    render: (v) => (v.cilindrata ? `${v.cilindrata} cc` : "—") },
  { key: "kw", label: "KW", sortField: "kw", defaultHidden: true,
    render: (v) => v.kw ?? "—" },
  { key: "cv", label: "CV", sortField: "kw", defaultHidden: true, // computed
    render: (v) => v.cv ?? "—" },
  { key: "trazione", label: "Trazione", sortField: "trazione", defaultHidden: true,
    render: (v) => VEHICLE_TRAZIONE_LABELS[v.trazione] || v.trazione || "—" },
  { key: "secondo_colore", label: "2° colore", sortField: "secondo_colore", defaultHidden: true,
    render: (v) => v.secondo_colore || "—" },
  { key: "tipo_colore", label: "Tipo colore", sortField: "tipo_colore", defaultHidden: true,
    render: (v) => VEHICLE_TIPO_COLORE_LABELS[v.tipo_colore] || "—" },
  { key: "n_proprietari", label: "N° propr.", sortField: "n_proprietari", defaultHidden: true,
    render: (v) => v.n_proprietari ?? "—" },
  { key: "prima_immatricolazione", label: "1^ immatr.", sortField: "prima_immatricolazione", defaultHidden: true,
    render: (v) => fmtDate(v.prima_immatricolazione) },

  // ---- hidden by default — Logistica ----
  { key: "deposito", label: "Deposito", sortField: "deposito_nome", defaultHidden: true,
    render: (v) => v.deposito_nome || "—" },
  { key: "n_chiave", label: "N° chiave", sortField: "n_chiave", defaultHidden: true,
    render: (v) => v.n_chiave || "—" },
  { key: "tipo_carico", label: "Tipo carico (esteso)", sortField: "tipo_carico", defaultHidden: true,
    render: (v) => VEHICLE_TIPO_CARICO_LABELS[v.tipo_carico] || v.tipo_carico || "—" },
  { key: "fornitore", label: "Fornitore", sortField: "fornitore_nome", defaultHidden: true,
    render: (v) => v.fornitore_nome || "—" },

  // ---- hidden by default — Acquisto/IVA ----
  // Sprint LISTE-2: aliquota dal codice di carico (funzione canonica lato server); ordinata per codice IVA
  { key: "aliquota_iva_acquisto", label: "Aliq. IVA", sortField: "iva_codice_carico", defaultHidden: true,
    render: (v) => (v.aliquota_iva_carico != null ? `${v.aliquota_iva_carico} %` : "—") },

  // ---- hidden by default — Importi ----
  { key: "prezzo_acquisto", label: "Prezzo acquisto", sortField: "importi.prezzo_acquisto", defaultHidden: true,
    render: (v) => formatCurrencyIT(v.importi?.prezzo_acquisto) },
  { key: "prezzo_pattuito", label: "Prezzo pattuito", sortField: "importi.prezzo_pattuito", defaultHidden: true,
    render: (v) => formatCurrencyIT(v.importi?.prezzo_pattuito) },
  { key: "listino", label: "Listino", sortField: "importi.listino", defaultHidden: true,
    render: (v) => formatCurrencyIT(v.importi?.listino) },
  { key: "margine_minimo", label: "Margine minimo", sortField: "importi.margine_minimo", defaultHidden: true,
    render: (v) => formatCurrencyIT(v.importi?.margine_minimo) },
  { key: "totale_costo_auto", label: "Totale costo auto", sortField: "importi.totale_costo_auto", defaultHidden: true,
    render: (v) => formatCurrencyIT(v.importi?.totale_costo_auto) },
  { key: "prezzo_vendita_minimo", label: "Prezzo di Vendita", sortField: "importi.prezzo_vendita_minimo", defaultHidden: true,
    render: (v) => formatCurrencyIT(v.importi?.prezzo_vendita_minimo) },
  { key: "delta_prezzo_su_acquisto", label: "Prezzo web - Totale Costi", sortField: "importi.delta_prezzo_su_acquisto", defaultHidden: true,
    render: (v) => {
      const x = v.importi?.delta_prezzo_su_acquisto;
      if (x == null) return "—";
      const cls = x > 0 ? "text-emerald-700" : x < 0 ? "text-red-600" : "text-slate-700";
      return <span className={cls}>{formatCurrencyIT(x)}</span>;
    } },
  { key: "recupero_su_promo", label: "Recupero su promo", sortField: "importi.recupero_su_promo", defaultHidden: true,
    render: (v) => {
      const x = v.importi?.recupero_su_promo;
      if (x == null) return "—";
      const cls = x > 0 ? "text-red-600" : "text-emerald-700";
      return <span className={cls}>{formatCurrencyIT(x)}</span>;
    } },

  // ---- hidden by default — Scadenze ----
  { key: "scadenza_tassa_proprieta", label: "Scad. bollo", sortField: "scadenza_tassa_proprieta", defaultHidden: true,
    render: (v) => fmtDate(v.scadenza_tassa_proprieta) },
  { key: "prossima_revisione_data", label: "Prox. revisione", sortField: "prossima_revisione_data", defaultHidden: true,
    render: (v) => fmtDate(v.prossima_revisione_data) },
  { key: "prossimo_tagliando", label: "Prox. tagliando", sortField: "prossimo_tagliando", defaultHidden: true,
    render: (v) => fmtDate(v.prossimo_tagliando) },
  { key: "garanzia_casa_madre_scadenza", label: "Scad. garanzia", sortField: "garanzia_casa_madre_scadenza", defaultHidden: true,
    render: (v) => fmtDate(v.garanzia_casa_madre_scadenza) },

  // ---- hidden by default — Meta ----
  { key: "created_by", label: "Creato da", sortField: "creato_da_nome", defaultHidden: true,
    render: (v, ctx) => {
      const u = ctx.users.find((x) => x.id === v.created_by);
      if (!u) return v.created_by ? <span className="text-slate-400">{String(v.created_by).slice(0, 8)}…</span> : "—";
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      return name || u.email || "—";
    } },
  { key: "note", label: "Note", sortField: null, defaultHidden: true,
    render: (v) => <span className="text-slate-600 text-xs" title={v.note || ""}>{truncate(v.note, 50)}</span> },
];

const DEFAULT_VISIBLE = COLUMN_DEFS.filter((c) => !c.defaultHidden).map((c) => c.key);
const DEFAULT_ORDER = COLUMN_DEFS.map((c) => c.key);
const DEFAULT_SORT = "data_carico:desc";

// Sprint 2B.2-A: raggruppamento Column Customizer in 6 categorie
const COLUMN_CATEGORIES = [
  { name: "Disponibilità e identificazione", keys: [
    "tipo_carico_badge", "disp_fisica", "stato_commerciale",
    "codice", "targa", "telaio", "prima_immatricolazione",
  ]},
  { name: "Caratteristiche", keys: [
    "marca", "modello", "versione", "allestimento",
    "cilindrata", "kw", "cv", "kw_cv",
    "carburante", "cambio", "trazione",
    "colore", "secondo_colore", "tipo_colore",
    "km", "n_proprietari", "anno",
  ]},
  { name: "Carico e logistica", keys: [
    "tipo_carico", "aliquota_iva_acquisto", "fornitore",
    "deposito", "n_chiave",
    "company", "site",
  ]},
  { name: "Importi", keys: [
    "listino", "prezzo_acquisto", "margine_minimo", "prezzo_vendita_minimo",
    "prezzo_web", "prezzo_pattuito",
    "totale_costo_auto", "delta_prezzo_su_acquisto", "recupero_su_promo",
  ]},
  { name: "Scadenze e manutenzioni", keys: [
    "scadenza_tassa_proprieta", "prossima_revisione_data",
    "prossimo_tagliando", "garanzia_casa_madre_scadenza",
  ]},
  { name: "Meta", keys: [
    "data_carico", "created_by", "giorni_stock", "note",
  ]},
];

// Sprint UI-GLOBALE — la lista WEB è questa stessa lista (colonne, filtri, preferenze) con i flag di canale.
const CANALE_LABEL = {
  sito_web: "Sito web", autoscout24: "AutoScout24", subito: "Subito.it",
  automobile_it: "Automobile.it", autosupermarket: "AutoSuperMarket",
};
const CANALE_KEYS = Object.keys(CANALE_LABEL).map((c) => `canale_${c}`);

export default function Vehicles({ mode = "veicoli" }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const elev = isElevated(user);
  const web = mode === "web";
  const confermaSemplice = useConfirm();
  const [mostraFoto, setMostraFoto] = useMostraFoto();
  const [contatori, setContatori] = useState([]);

  // Filters (default: tutti i multi-select vuoti = "Tutte/i")
  // Sprint 4Z.1 D3: default stato_commerciale = ["__none__"] (Vendibile)
  // Sprint DASHBOARD-FIX — filtri esistenti leggibili dall'indirizzo (stato_commerciale, giorni_stock_min/max)
  const [urlParams, setUrlParams] = useSearchParams();
  const [filters, setFilters] = useState(() => ({
    company_id: [], site_id: [], status: [], stato: [],
    stato_commerciale: urlParams.getAll("stato_commerciale").length ? urlParams.getAll("stato_commerciale") : (web ? [] : ["__none__"]),
    marca: [], carburante: [], cambio: [],
    prezzo_web_min: "", prezzo_web_max: "",
    km_min: "", km_max: "",
    giorni_stock_min: urlParams.get("giorni_stock_min") || "", giorni_stock_max: urlParams.get("giorni_stock_max") || "",
    q: "",
  }));
  const [showFilters, setShowFilters] = useState(true);

  // Data
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [allSites, setAllSites] = useState([]); // for column resolution (site name lookup)
  const [marche, setMarche] = useState([]);
  const [users, setUsers] = useState([]);         // for "Creato da" column

  // Bumped on Reset filtri to remount Selects (Radix uncontrolled state reset).
  const [selectsKey, setSelectsKey] = useState(0);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Sprint LISTE-2: l'ordinamento salvato è lo stato iniziale (non un secondo comando dopo il montaggio)
  const [sort, setSort] = useState(() => {
    const saved = user?.preferenze_vista_veicoli?.ordinamento_corrente;
    return saved?.field ? `${saved.field}:${saved.dir || "asc"}` : DEFAULT_SORT;
  });
  const nuovaRichiesta = useUltimaRichiesta();
  const [loading, setLoading] = useState(false);

  // ----- Column preferences (Sprint 2B.1) -----
  const initialPrefs = user?.preferenze_vista_veicoli || {};
  // Sprint DASHBOARD A.1/A.2 — la lista WEB ha chiavi proprie e di partenza mette i canali subito dopo la foto
  const kVis = web ? "colonne_visibili_web" : "colonne_visibili";
  const kOrd = web ? "ordine_colonne_web" : "ordine_colonne";
  const defVisible = web ? [...CANALE_KEYS, ...DEFAULT_VISIBLE] : DEFAULT_VISIBLE;
  const defOrder = web ? [...CANALE_KEYS, ...DEFAULT_ORDER] : DEFAULT_ORDER;
  // Sprint 4Z.2.1: auto-migration delle preferenze legacy che non hanno
  // le nuove colonne (tipo_carico_badge, disp_fisica, stato_commerciale).
  // Le nuove colonne devono apparire come PRIME a sinistra.
  const NEW_4Z2_KEYS = ["tipo_carico_badge", "disp_fisica", "stato_commerciale"];
  const migrateOrder = (saved) => {
    if (!saved?.length) return defOrder;
    if (web) return [...saved, ...defOrder.filter((k) => !saved.includes(k))];
    // Sprint 4Z.2.1: le 3 colonne 4Z.2 vanno in testa SOLO se mancano dalle preferenze salvate;
    // Sprint LISTE-2: se l'utente le ha già posizionate, la sua scelta resta (rimuove "status" legacy).
    const cleaned = saved.filter((k) => k !== "status");
    const missing = NEW_4Z2_KEYS.filter((k) => !cleaned.includes(k));
    return [...missing, ...cleaned];
  };
  const migrateVisible = (saved) => {
    if (!saved?.length) return defVisible;
    if (web) return saved;
    const arr = saved.filter((k) => k !== "status");
    const missing = NEW_4Z2_KEYS.filter((k) => !arr.includes(k));
    return missing.length ? [...missing, ...arr] : arr;
  };
  const [visibleCols, setVisibleCols] = useState(() => migrateVisible(initialPrefs[kVis]));
  const [orderCols, setOrderCols] = useState(() => migrateOrder(initialPrefs[kOrd]));

  // Sprint 4Z.2.1: resize colonne — persistito in localStorage
  const widthsLsKey = web ? "web_column_widths" : "vehicles_column_widths";
  const [colWidths, setColWidths] = useState(() => {
    try { return JSON.parse(localStorage.getItem(widthsLsKey) || "{}"); }
    catch { return {}; }
  });
  const persistWidth = (key, w) => {
    setColWidths((prev) => {
      const next = { ...prev, [key]: Math.max(60, w) };
      try { localStorage.setItem(widthsLsKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const resetWidths = () => {
    setColWidths({});
    try { localStorage.removeItem(widthsLsKey); } catch {}
  };

  // Load companies + marche + suppliers + users on mount (for column resolution)
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
    api.get("/vehicles/marche").then((r) => setMarche(r.data?.items || [])).catch(() => {});
    // /users richiede admin; se 403 non blocchiamo nulla
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  // Sites depend on selected companies (for the filter dropdown)
  useEffect(() => {
    const cids = filters.company_id;
    if (!cids.length) { setSites([]); return; }
    Promise.all(cids.map((cid) => api.get(`/companies/${cid}/sites`).then((r) => r.data || [])))
      .then((arrs) => setSites(arrs.flat()))
      .catch(() => setSites([]));
  }, [filters.company_id]);

  // Load ALL sites (for column lookup, regardless of selected company)
  useEffect(() => {
    if (!companies.length) return;
    Promise.all(companies.map((c) => api.get(`/companies/${c.id}/sites`).then((r) => r.data || []).catch(() => [])))
      .then((arrs) => setAllSites(arrs.flat()));
  }, [companies]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach((x) => x && params.append(k, x));
      else if (v !== "" && v != null) params.append(k, v);
    });
    params.append("page", String(page));
    params.append("page_size", String(pageSize));
    params.append("sort", sort);
    if (web) params.append("con_canali", "true");
    const ultima = nuovaRichiesta();
    try {
      const r = await api.get(`/vehicles?${params.toString()}`);
      if (!ultima()) return;
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } finally {
      if (ultima()) setLoading(false);
    }
  }, [filters, page, pageSize, sort, web, nuovaRichiesta]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const loadContatori = useCallback(() => {
    if (!web) return;
    api.get("/web/contatori").then((r) => setContatori(r.data?.contatori || [])).catch(() => setContatori([]));
  }, [web]);
  useEffect(() => { loadContatori(); }, [loadContatori]);

  const isPub = (row, canale) =>
    (row.portali_pubblicazione || []).some((p) => p.canale === canale && p.pubblicato);
  const togglePubblicazione = async (row, canale) => {
    const next = !isPub(row, canale);
    const label = CANALE_LABEL[canale] || canale;
    // Sprint MINI-5 — una sola conferma sia per pubblicare sia per rimuovere
    const ok = await confermaSemplice({
      variant: next ? "approve" : "delete",
      title: next ? `Pubblica su ${label}` : `Rimuovi da ${label}`,
      message: next
        ? `Stai pubblicando questo veicolo su ${label}.\nSei sicuro?`
        : `Stai rimuovendo questo veicolo da ${label}.\nSei sicuro?`,
      detail: [row.marca, row.modello, row.targa].filter(Boolean).join(" "),
      confirmLabel: next ? "Sì, continua" : "Sì, rimuovi",
    });
    if (!ok) return;
    try {
      await api.patch(`/vehicles/${row.id}/pubblicazione`, { canale, pubblicato: next });
      toast.success(next ? "Pubblicato" : "Rimosso");
      fetchList();
      loadContatori();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Errore");
    }
  };

  const resetFilters = () => {
    if ([...urlParams.keys()].length) setUrlParams({}, { replace: true });
    setFilters({
      company_id: [], site_id: [], status: [], stato: [], stato_commerciale: [],
      marca: [], carburante: [], cambio: [],
      prezzo_web_min: "", prezzo_web_max: "",
      km_min: "", km_max: "",
      giorni_stock_min: "", giorni_stock_max: "",
      q: "",
    });
    setPage(1);
    // Force remount of all Radix Selects so their internal value resets to placeholder.
    setSelectsKey((k) => k + 1);
  };

  // ----- Persist column prefs (debounced) -----
  const saveTimer = useRef(null);
  const persistPrefs = useCallback((patch) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const r = await api.patch("/users/me/preferenze-vista-veicoli", patch);
        if (r.data?.preferenze_vista_veicoli && setUser) {
          setUser((u) => {
            if (!u) return u;
            const next = { ...u, preferenze_vista_veicoli: r.data.preferenze_vista_veicoli };
            try { localStorage.setItem("dms_user", JSON.stringify(next)); } catch {}
            return next;
          });
        }
      } catch (e) { /* silent */ }
    }, 400);
  }, [setUser]);

  const onColumnsChange = ({ order, visible }) => {
    setOrderCols(order);
    setVisibleCols(visible);
    persistPrefs({ [kVis]: visible, [kOrd]: order });
  };
  const resetColumns = () => {
    setOrderCols(defOrder);
    setVisibleCols(defVisible);
    persistPrefs({ [kVis]: defVisible, [kOrd]: defOrder });
  };

  // ----- Sort tri-state (none → asc → desc → none) -----
  const onHeaderSort = (sortField) => {
    if (!sortField) return;
    let next;
    const [curField, curDir] = sort.split(":");
    if (curField !== sortField) next = `${sortField}:asc`;
    else if (curDir === "asc") next = `${sortField}:desc`;
    // Sprint RIFINITURE — sulla colonna dell'ordine predefinito (Data carico) il terzo clic non tornava mai a crescente
    else next = sortField === DEFAULT_SORT.split(":")[0] ? `${sortField}:asc` : DEFAULT_SORT;
    setSort(next);
    setPage(1);
    if (next === DEFAULT_SORT) {
      persistPrefs({ ordinamento_corrente: null });
    } else {
      const [f, d] = next.split(":");
      persistPrefs({ ordinamento_corrente: { field: f, dir: d } });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Helper for multi-select toggle
  const togglePick = (key, val) => {
    setFilters((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
    setPage(1);
  };

  // Resolve which columns to display in which order (only those visible AND known)
  const allDefs = useMemo(() => (web ? [...CANALE_KEYS.map((k) => ({
    key: k, label: CANALE_LABEL[k.slice(7)], width: 110,
    render: (v) => ((v.canali_attivi || []).includes(k.slice(7)) ? (
      <Checkbox checked={isPub(v, k.slice(7))} onCheckedChange={() => togglePubblicazione(v, k.slice(7))}
        data-testid={`web-flag-${v.id}-${k.slice(7)}`} />
    ) : <span className="text-slate-300" title="Canale non attivo">—</span>),
  })), ...COLUMN_DEFS] : COLUMN_DEFS),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [web, items]);
  const colsByKey = useMemo(() => Object.fromEntries(allDefs.map((c) => [c.key, c])), [allDefs]);
  // canali non attivi per nessuna azienda dell'elenco: colonna non mostrata (resta nel selettore)
  const canaliPresenti = useMemo(() => new Set(items.flatMap((r) => (r.canali_attivi || []).map((c) => `canale_${c}`))), [items]);
  const renderedCols = useMemo(() => {
    const visSet = new Set(visibleCols);
    return orderCols
      .filter((k) => visSet.has(k) && colsByKey[k] && (!k.startsWith("canale_") || canaliPresenti.has(k)))
      .map((k) => colsByKey[k]);
  }, [orderCols, visibleCols, colsByKey, canaliPresenti]);
  // Sprint DASHBOARD-FIX-2 — larghezza: misura a mano dell'utente, altrimenti adattata al contenuto
  const tableRef = useRef(null);
  const autoW = useLarghezzeAuto(tableRef, [items, renderedCols, mostraFoto]);
  const larg = (col) => colWidths[col.key] ?? autoW[col.key] ?? col.width;
  const larghezzaTot = renderedCols.reduce((t, c) => t + (larg(c) || 120), mostraFoto ? 84 : 0);

  const ctx = { companies, allSites, users };
  const [curSortField, curSortDir] = sort.split(":");

  return (
    <div className="space-y-5" data-testid={web ? "web-page" : "vehicles-page"}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          {web ? (
            <>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                <Globe className="h-6 w-6 text-blue-600" /> WEB
              </h1>
              <p className="text-slate-500 mt-1">Governa la pubblicazione dei veicoli sui canali attivi.</p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">Veicoli</h1>
              <p className="text-slate-500 mt-1">Stock veicoli e disponibilità.</p>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <MostraFotoToggle mostra={mostraFoto} onChange={setMostraFoto} />
          <Button variant="outline" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn">
            <Filter className="h-4 w-4 mr-1.5" /> {showFilters ? "Nascondi filtri" : "Mostra filtri"}
          </Button>
          <VehicleColumnsButton
            allColumns={allDefs.map((c) => ({ key: c.key, label: c.label }))}
            order={orderCols}
            visible={visibleCols}
            categories={web ? [{ name: "Canali di pubblicazione", keys: CANALE_KEYS }, ...COLUMN_CATEGORIES] : COLUMN_CATEGORIES}
            onChange={onColumnsChange}
            onReset={resetColumns}
            onResetWidths={resetWidths}
          />
          {elev && !web && (
            <Button variant="outline" onClick={() => navigate("/vehicles/cestino")} data-testid="cestino-btn">
              <Trash2 className="h-4 w-4 mr-1.5" /> Cestino
            </Button>
          )}
          {!web && (
            <Button onClick={() => navigate("/vehicles/new")} className="bg-blue-600 hover:bg-blue-700"
              data-testid="new-vehicle-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Nuovo veicolo
            </Button>
          )}
        </div>
      </div>

      {web && (
        <div className="flex flex-wrap gap-3" data-testid="web-contatori">
          {contatori.length === 0 ? (
            <span className="text-sm text-slate-400">Nessun canale attivo.</span>
          ) : contatori.map((c) => (
            <div key={c.canale} className="px-4 py-2 rounded-lg border border-slate-200 bg-white" data-testid={`web-contatore-${c.canale}`}>
              <div className="text-xs text-slate-500">{c.label}</div>
              <div className="text-lg font-semibold text-slate-900">{c.count}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters panel */}
      {showFilters && (
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Ricerca */}
            <div className="lg:col-span-2">
              <Label>Ricerca libera</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  className="pl-8"
                  placeholder="Targa, telaio, marca, modello, versione..."
                  {...attrRicerca(web ? "web" : "veicoli")}
                  value={filters.q}
                  onChange={(e) => { setFilters((f) => ({ ...f, q: e.target.value })); setPage(1); }}
                  data-testid="filter-q"
                />
              </div>
            </div>
            {/* Sprint 4X.4 E4: 2 filtri separati 2-assi */}
            <div>
              <Label>Disponibilità Fisica</Label>
              <div className="flex flex-wrap gap-1 mt-1" data-testid="filter-disp-fisica-row">
                {[
                  ["VIRTUAL", "Virtuale"],
                  ["INCOMING", "In arrivo"],
                  ["AVAILABLE", "Disponibile"],
                ].map(([k, label]) => (
                  <button
                    key={k} type="button"
                    onClick={() => togglePick("status", k)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      filters.status.includes(k)
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`filter-dispfisica-${k}`}
                  >{label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Stato Commerciale</Label>
              <div className="flex flex-wrap gap-1 mt-1" data-testid="filter-stato-comm-row">
                {[
                  ["__none__", "Vendibile"],
                  ["impegnata", "Impegnata"],
                  ["venduta", "Venduta"],
                  ["consegnata", "Consegnata"],
                ].map(([k, label]) => (
                  <button
                    key={k} type="button"
                    onClick={() => togglePick("stato_commerciale", k)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      (filters.stato_commerciale || []).includes(k)
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`filter-statocomm-${k}`}
                  >{label}</button>
                ))}
              </div>
            </div>
            {/* Stato (etichetta «Condizioni», come nella scheda veicolo) */}
            <div>
              <Label>Condizioni</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {Object.entries(VEHICLE_STATO_LABELS).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => togglePick("stato", k)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      filters.stato.includes(k)
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}>{v}</button>
                ))}
              </div>
            </div>
            {/* Azienda */}
            <div>
              <Label>Azienda</Label>
              <Select key={`company-${selectsKey}`} onValueChange={(val) => { togglePick("company_id", val); }}>
                <SelectTrigger><SelectValue placeholder="Tutte le aziende" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {filters.company_id.includes(c.id) ? "✓ " : ""}{c.nome_breve || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filters.company_id.length > 0 && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Selezionate: {filters.company_id.length} (clicca di nuovo per rimuovere)
                </div>
              )}
            </div>
            {/* Sede */}
            <div>
              <Label>Sede</Label>
              <Select key={`site-${selectsKey}`} onValueChange={(val) => togglePick("site_id", val)} disabled={!sites.length}>
                <SelectTrigger><SelectValue placeholder={sites.length ? "Tutte le sedi" : "(seleziona azienda)"} /></SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {filters.site_id.includes(s.id) ? "✓ " : ""}{s.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Marca */}
            <div>
              <Label>Marca</Label>
              <Select key={`marca-${selectsKey}`} onValueChange={(val) => togglePick("marca", val)} disabled={!marche.length}>
                <SelectTrigger><SelectValue placeholder="Tutte le marche" /></SelectTrigger>
                <SelectContent>
                  {marche.map((m) => (
                    <SelectItem key={m} value={m}>
                      {filters.marca.includes(m) ? "✓ " : ""}{m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Carburante */}
            <div>
              <Label>Carburante</Label>
              <Select key={`carb-${selectsKey}`} onValueChange={(val) => togglePick("carburante", val)}>
                <SelectTrigger><SelectValue placeholder="Tutti i carburanti" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(VEHICLE_CARBURANTE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {filters.carburante.includes(k) ? "✓ " : ""}{v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Cambio */}
            <div>
              <Label>Cambio</Label>
              <Select key={`cambio-${selectsKey}`} onValueChange={(val) => togglePick("cambio", val)}>
                <SelectTrigger><SelectValue placeholder="Tutti i cambi" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(VEHICLE_CAMBIO_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {filters.cambio.includes(k) ? "✓ " : ""}{v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Range prezzo vendita (alias di prezzo web) */}
            <div>
              <Label>Prezzo di Vendita (€)</Label>
              <div className="flex gap-1" data-testid="filter-prezzo-vendita-range">
                <Input type="number" placeholder="da" value={filters.prezzo_web_min}
                  onChange={(e) => { setFilters((f) => ({ ...f, prezzo_web_min: e.target.value })); setPage(1); }}
                  data-testid="filter-prezzo-vendita-min" />
                <Input type="number" placeholder="a" value={filters.prezzo_web_max}
                  onChange={(e) => { setFilters((f) => ({ ...f, prezzo_web_max: e.target.value })); setPage(1); }}
                  data-testid="filter-prezzo-vendita-max" />
              </div>
            </div>
            <div>
              <Label>Km</Label>
              <div className="flex gap-1">
                <Input type="number" placeholder="min" value={filters.km_min}
                  onChange={(e) => { setFilters((f) => ({ ...f, km_min: e.target.value })); setPage(1); }} />
                <Input type="number" placeholder="max" value={filters.km_max}
                  onChange={(e) => { setFilters((f) => ({ ...f, km_max: e.target.value })); setPage(1); }} />
              </div>
            </div>
            <div>
              <Label>Giorni stock</Label>
              <div className="flex gap-1">
                <Input type="number" placeholder="min" value={filters.giorni_stock_min}
                  onChange={(e) => { setFilters((f) => ({ ...f, giorni_stock_min: e.target.value })); setPage(1); }} />
                <Input type="number" placeholder="max" value={filters.giorni_stock_max}
                  onChange={(e) => { setFilters((f) => ({ ...f, giorni_stock_max: e.target.value })); setPage(1); }} />
              </div>
            </div>
            <div className="flex items-end">
              <Button variant="ghost" onClick={resetFilters} data-testid="reset-filters-btn">Reset filtri</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card>
        {loading ? (
          <div className="p-8 text-center text-slate-500">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <CarIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-700 font-medium">Nessun veicolo trovato</div>
            <div className="text-slate-500 text-sm mt-1">Crea un nuovo veicolo o modifica i filtri.</div>
            <div className="flex gap-2 justify-center mt-4">
              {!web && (
                <Button onClick={() => navigate("/vehicles/new")} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-1.5" /> Nuovo veicolo
                </Button>
              )}
              <Button variant="outline" onClick={resetFilters}>Reset filtri</Button>
            </div>
          </div>
        ) : (
          <StickyHScroll testid={web ? "web-hscroll" : "vehicles-hscroll"}>
            <table ref={tableRef} className="w-full text-sm lista-uniforme" style={{ tableLayout: "fixed", minWidth: larghezzaTot }}>
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase text-slate-500">
                  {mostraFoto && <th className="p-2" style={{ width: 84 }} data-testid="th-foto">Foto</th>}
                  {renderedCols.map((col) => {
                    const isSortable = !!col.sortField;
                    const isActive = isSortable && curSortField === col.sortField;
                    return (
                      <Th
                        key={col.key}
                        onClick={isSortable ? () => onHeaderSort(col.sortField) : undefined}
                        active={isActive}
                        dir={isActive ? curSortDir : null}
                        sortable={isSortable}
                        testid={`th-${col.key}`}
                        width={larg(col)}
                        onResize={persistWidth}
                        colKey={col.key}
                      >
                        {col.label}
                      </Th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id} {...rigaCliccabile(() => navigate(`/vehicles/${v.id}`))}
                    className={`border-b border-slate-100 hover:bg-slate-50/60 ${RIGA_CLS}`}
                    data-testid={web ? `web-row-${v.id}` : `vehicle-row-${v.codice}`}>
                    {mostraFoto && <Td width={84}><CopertinaThumb vehicleId={v.id} coverId={copertinaId(v.foto)} /></Td>}
                    {renderedCols.map((col) => (
                      <Td key={col.key} colKey={col.key} width={larg(col)}>
                        <Cella servizio={SERVIZIO.has(col.key)}>{col.render(v, ctx)}</Cella>
                      </Td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </StickyHScroll>
        )}

        {/* Pagination */}
        {items.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-t border-slate-200 text-sm">
            <div className="text-slate-500">
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} di {total}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Per pagina</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>«</Button>
              <span className="px-2">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>»</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function Th({ children, onClick, active, dir, sortable, testid, width, onResize, colKey }) {
  let arrow = null;
  if (sortable) {
    if (active && dir === "asc") arrow = <ChevronUp className="h-3 w-3" />;
    else if (active && dir === "desc") arrow = <ChevronDown className="h-3 w-3" />;
    else arrow = <ChevronsUpDown className="h-3 w-3 opacity-30 group-hover:opacity-60" />;
  }
  return (
    <th
      onClick={onClick}
      data-testid={testid}
      style={width ? { width, minWidth: 60 } : undefined}
      data-col={colKey}
      className={`p-2 group relative whitespace-nowrap overflow-hidden ${onClick ? "cursor-pointer hover:text-slate-700 select-none" : ""} ${active ? "text-slate-900" : ""}`}
    >
      <span className="th-label inline-flex items-center gap-1">
        {children}
        {arrow}
      </span>
      {/* Sprint 4Z.2.1: resize handle */}
      {onResize && (
        <span
          onMouseDown={(e) => {
            e.stopPropagation();
            const startX = e.clientX;
            const th = e.currentTarget.parentElement;
            const startW = th.getBoundingClientRect().width;
            const onMove = (ev) => onResize(colKey, startW + (ev.clientX - startX));
            const onUp = () => {
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onUp);
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
          }}
          onClick={(e) => e.stopPropagation()}
          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-300 active:bg-blue-500"
          data-testid={`th-resize-${colKey}`}
        />
      )}
    </th>
  );
}
function Td({ children, className = "", width, colKey }) {
  return <td style={width ? { width } : undefined} data-col={colKey}
              className={`p-2 align-top ${className}`}>{children}</td>;
}
